import { useNavigate } from "react-router-dom";
import { pagesForSources, employeeVisiblePages } from "@prism/shared/modules";
import { Card, PageError, PageLoading } from "../components/index.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

/**
 * EMPLOYEE landing page. Shows only the clients assigned to this team member
 * (their clientScopes), each as a card with a division badge, sync status, and
 * quick links to the dashboard tabs they're scoped to see. No admin controls,
 * no client switcher — clicking a card opens the client's staff cockpit
 * (Agency Cockpit v1); the quick links still view-as straight into a tab.
 */
const STATUS_DOT = { active: "#16a34a", error: "#dc2626", "not synced": "#a3a3a3" };

export function MyClients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dataScopes = user?.dataScopes || [];
  const { data, loading, error, reload } = useApi("/api/me/clients");
  // Alphabetical, matching the switcher and the admin list.
  const clients = (data?.clients || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  const activity = data?.recentActivity || [];

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading) return <PageLoading />;

  // Dashboard tabs this employee can open for a given client.
  function quickLinks(client) {
    const sourceTypes = (client.dataSources || []).map((d) => d.type);
    const visible = employeeVisiblePages(dataScopes);
    return pagesForSources(sourceTypes).filter((p) => p.group !== "account" && visible.has(p.key));
  }

  return (
    <div className="max-w-[1100px]">
      <div className="mb-4">
        <h2 className="text-[18px] font-semibold text-text-primary leading-tight">My Clients</h2>
        <p className="text-[12px] text-text-muted mt-0.5">
          {`${clients.length} assigned ${clients.length === 1 ? "client" : "clients"}`}
          {user?.jobTitle ? ` · ${user.jobTitle}` : ""}
        </p>
      </div>

      {clients.length === 0 && (
        <Card>
          <div className="px-5 py-10 text-center text-[13px] text-text-muted">
            No clients assigned to you yet. Your administrator can add clients to your scope under Team.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {clients.map((c) => (
          <Card key={c.id} className="p-0">
            <button
              type="button"
              onClick={() => navigate(`/clients/${c.id}`)}
              className="w-full text-left px-5 py-4 hover:bg-[var(--color-border-light)] transition-colors rounded-t-card"
            >
              <div className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-full border border-border-default flex-shrink-0" style={{ backgroundColor: c.accentColor || "#e5e5e5" }} />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-text-primary truncate">{c.name}</div>
                  {c.division && (
                    <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wide text-text-secondary bg-app-bg border border-border-default rounded px-1.5 py-0.5">
                      {c.division.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[12px] text-text-secondary">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_DOT[c.syncStatus] || "#a3a3a3" }} />
                  {c.syncStatus}
                </span>
                <span className="text-text-muted">· Updated {timeAgo(c.lastSync)}</span>
                {c.billing?.overdueCount > 0 && (
                  <span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium" style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}>Unpaid · {c.billing.overdueDays}d</span>
                )}
              </div>
            </button>
            <div className="px-5 pb-4 pt-1 flex flex-wrap gap-1.5 border-t border-border-light">
              {quickLinks(c).map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => navigate(`/${p.key}?clientId=${c.id}`)}
                  className="text-[11px] text-accent hover:underline font-medium"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {activity.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[13px] font-semibold text-text-primary mb-2">Recent Activity</h3>
          <Card className="p-0">
            <div className="px-5 py-3 space-y-2">
              {activity.map((a, i) => (
                <div key={i} className="text-[12px] text-text-secondary">
                  <span className="text-text-primary">{a.description}</span>
                  <span className="text-text-muted"> · {timeAgo(a.occurredAt)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
